import { useState } from "react";
import { toast } from "sonner";
import { Copy, KeyRound, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import {
  FormCard,
  FormStackedRow,
  InlineMessage,
  SettingsHeader,
} from "@/components/settings/settings-ui";
import { formatRelativeTime } from "@/utils";
import {
  useDeleteApiKeyMutation,
  useMintApiKeyMutation,
  useWorkspaceApiKeysQuery,
  type MintedApiKey,
  type WorkspaceApiKeyRow,
} from "@/data/api-keys";

interface ApiKeysSectionProps {
  workspaceSlug?: string | null;
  canManage: boolean;
}

export function ApiKeysSection({
  workspaceSlug,
  canManage,
}: ApiKeysSectionProps) {
  const keysQuery = useWorkspaceApiKeysQuery(workspaceSlug);
  const mintMutation = useMintApiKeyMutation(workspaceSlug ?? null);
  const deleteMutation = useDeleteApiKeyMutation(workspaceSlug ?? null);
  const [name, setName] = useState("");
  const [minted, setMinted] = useState<MintedApiKey | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Deletion is irreversible (the token stops working instantly), so the
  // button arms on first click and deletes on the second.
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const keys = keysQuery.data?.api_keys ?? [];

  async function mintKey() {
    const trimmed = name.trim();
    if (!workspaceSlug || !trimmed) return;
    setBusyId("mint");
    setError(null);
    try {
      const result = await mintMutation.mutateAsync(trimmed);
      setMinted(result);
      setName("");
      toast.success("API key created");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      toast.error("Failed to create API key");
    } finally {
      setBusyId(null);
    }
  }

  async function deleteKey(key: WorkspaceApiKeyRow) {
    setBusyId(key.id);
    setConfirmId(null);
    setError(null);
    try {
      await deleteMutation.mutateAsync(key.id);
      if (minted?.api_key.id === key.id) setMinted(null);
      toast.success("API key deleted");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      toast.error("Failed to delete API key");
    } finally {
      setBusyId(null);
    }
  }

  async function copyToken(token: string) {
    try {
      await navigator.clipboard.writeText(token);
      toast.success("API key copied");
    } catch {
      toast.error("Copy failed — select the key text and copy manually");
    }
  }

  return (
    <div className="mx-auto max-w-3xl">
      <SettingsHeader
        title="API"
        description="API keys authenticate the transcript pull API (/api/v1) as this workspace. Send one as an Authorization: Bearer header."
      />

      <div className="space-y-6">
        {error ? <InlineMessage variant="error">{error}</InlineMessage> : null}

        {canManage ? (
          <FormCard>
            <FormStackedRow
              label="Create key"
              hint="A key can read every processed capture in this workspace. The full key is shown once, right after it's created."
            >
              <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_auto]">
                <Input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      void mintKey();
                    }
                  }}
                  placeholder="What's this key for? e.g. n8n sync"
                />
                <Button
                  type="button"
                  disabled={busyId === "mint" || !name.trim()}
                  onClick={() => void mintKey()}
                >
                  {busyId === "mint" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <KeyRound className="h-3.5 w-3.5" />
                  )}
                  Create key
                </Button>
              </div>
            </FormStackedRow>
          </FormCard>
        ) : (
          <InlineMessage variant="info">
            Only workspace admins can create or delete API keys.
          </InlineMessage>
        )}

        {minted ? (
          <InlineMessage variant="success">
            <div className="space-y-2">
              <div>
                Copy your new key <strong>{minted.api_key.name}</strong> now —
                it won't be shown again.
              </div>
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded-md border border-border bg-background px-2.5 py-1.5 font-mono text-[12px] text-foreground">
                  {minted.token}
                </code>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => void copyToken(minted.token)}
                >
                  <Copy className="h-3.5 w-3.5" />
                  Copy
                </Button>
              </div>
            </div>
          </InlineMessage>
        ) : null}

        <FormCard>
          <FormStackedRow label="Keys">
            {keysQuery.isPending && workspaceSlug ? (
              <div className="flex items-center gap-2 py-3 text-[13px] text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading API keys
              </div>
            ) : keys.length === 0 ? (
              <div className="border-t border-border py-3 text-[13px] text-muted-foreground">
                No API keys yet.
              </div>
            ) : (
              <div className="divide-y divide-border border-t border-border">
                {keys.map((key) => (
                  <ApiKeyRow
                    key={key.id}
                    apiKey={key}
                    busy={busyId === key.id}
                    armed={confirmId === key.id}
                    canManage={canManage}
                    onArm={(id) => setConfirmId(id)}
                    onDelete={deleteKey}
                  />
                ))}
              </div>
            )}
          </FormStackedRow>
        </FormCard>
      </div>
    </div>
  );
}

function ApiKeyRow({
  apiKey,
  busy,
  armed,
  canManage,
  onArm,
  onDelete,
}: {
  apiKey: WorkspaceApiKeyRow;
  busy: boolean;
  armed: boolean;
  canManage: boolean;
  onArm: (id: string | null) => void;
  onDelete: (key: WorkspaceApiKeyRow) => Promise<void>;
}) {
  return (
    <div className="grid items-center gap-3 py-3 md:grid-cols-[minmax(0,1fr)_auto]">
      <div className="min-w-0">
        <div className="truncate text-[13px] font-medium text-foreground">
          {apiKey.name}
        </div>
        <div className="truncate text-[12px] text-muted-foreground">
          <span className="font-mono">{apiKey.token_prefix}…</span> · created{" "}
          {formatApiKeyDate(apiKey.created_at)} · last used{" "}
          {formatLastUsed(apiKey.last_used_at)}
        </div>
      </div>
      {canManage ? (
        <Button
          type="button"
          size="sm"
          variant={armed ? "destructive" : "outline"}
          disabled={busy}
          onClick={() => (armed ? void onDelete(apiKey) : onArm(apiKey.id))}
          onBlur={() => armed && onArm(null)}
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Trash2 className="h-3.5 w-3.5" />
          )}
          {armed ? "Confirm delete" : "Delete"}
        </Button>
      ) : (
        <div />
      )}
    </div>
  );
}

function formatApiKeyDate(value: string | number): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "recently";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatLastUsed(value: string | number | null): string {
  if (value == null) return "never";
  const ms = new Date(value).getTime();
  if (Number.isNaN(ms)) return "never";
  return formatRelativeTime(ms);
}
